/**
 * Navigation logic for the storage hierarchy browser.
 *
 * The browser shows ONE level at a time rather than a nested tree, so its
 * height is a function of how many children a node has, never of how big the
 * tree is. A twenty-rack dewar and a two-rack one look the same.
 *
 * Pure and here rather than in the component because this repo's vitest is
 * node-only: anything left in a `.tsx` has no coverage at all.
 */

import { isOccupiable, pathSegments } from './storageTree'
import type { ApiStorageNode } from '@/types/storage'

/**
 * A node that may know how many containers are inside it.
 *
 * The tree endpoint sends that count for every container, including ones whose
 * children it deliberately left out. Optional because the same functions serve
 * lists that were never fetched that way — a locally held set of nodes answers
 * the question by looking at itself, which is correct when it holds everything.
 */
type CountedNode = ApiStorageNode & { childCount?: number }

/**
 * ─── Why there is an index ────────────────────────────────────────────────
 *
 * Every function here used to answer its question by scanning the whole node
 * array. That is fine for one call and quadratic for a tree: `containerTree`
 * called `browseChildren` once per container, so a unit with C containers and
 * N nodes cost O(C·N) — and `StorageBrowser` derives the tree three times per
 * render (the tree itself, the expand-all id list, and the reset effect).
 *
 * Measured on the shapes the backend actually permits (its own ceiling is
 * MAX_LAYOUT_NODES = 100,000 for a single unit):
 *
 *     5,111 nodes /   111 containers →     8 ms per tree build
 *    25,521 nodes /   521 containers →   353 ms
 *    49,641 nodes / 1,641 containers → 2,890 ms
 *    97,551 nodes / 2,551 containers → 2,681 ms  (≈7.2 s per render)
 *
 * Five times the nodes cost forty-five times the time. Seven seconds of
 * blocking work on the main thread per render is the freeze this index exists
 * to remove.
 *
 * The index is built ONCE per node array and cached against that array's
 * identity, so the three derivations in a render share one build and a render
 * that changes nothing rebuilds nothing. A `WeakMap` is what makes that safe to
 * leave in place: when the query result is replaced, its index becomes
 * collectable with it rather than accumulating a copy of every tree the user
 * has looked at.
 *
 * Every public function below keeps the signature it had, so this is invisible
 * to callers — the array stays the argument, and the index is derived from it.
 */
interface StorageIndex {
  byId: Map<string, ApiStorageNode>
  /** Materialised path → node. What resolves an ancestor without a scan. */
  byPath: Map<string, ApiStorageNode>
  /** parentId → its container children, sorted once at build time. */
  containerChildren: Map<string, ApiStorageNode[]>
}

/**
 * One collator for every sibling sort in this module.
 *
 * `localeCompare(other, undefined, { numeric: true })` can construct a collator
 * per call, and sorting the containers of a large freezer is tens of thousands
 * of calls. Naming it once is the difference between a sort and a sort plus ten
 * thousand object constructions.
 */
const NATURAL = new Intl.Collator(undefined, { numeric: true })

const indexCache = new WeakMap<ApiStorageNode[], StorageIndex>()

function indexOf(nodes: ApiStorageNode[]): StorageIndex {
  const cached = indexCache.get(nodes)
  if (cached) return cached

  const byId = new Map<string, ApiStorageNode>()
  const byPath = new Map<string, ApiStorageNode>()
  const containerChildren = new Map<string, ApiStorageNode[]>()

  for (let i = 0; i < nodes.length; i++) {
    const n = nodes[i]
    byId.set(n.id, n)
    byPath.set(n.path, n)
    // Positions are excluded here rather than at every read, so the exclusion
    // is stated once and no caller can forget it.
    if (isOccupiable(n.kind) || n.parentId == null) continue
    const siblings = containerChildren.get(n.parentId)
    if (siblings) siblings.push(n)
    else containerChildren.set(n.parentId, [n])
  }

  // Sorted once per bucket at build time instead of on every read. Summed over
  // the tree this touches each container exactly once.
  for (const siblings of containerChildren.values()) {
    siblings.sort((a, b) => NATURAL.compare(a.path, b.path))
  }

  const index: StorageIndex = { byId, byPath, containerChildren }
  indexCache.set(nodes, index)
  return index
}

/**
 * The children to list under a node.
 *
 * Positions are excluded on purpose: a 9×9 box would add 81 rows to a panel
 * whose whole point is to stay short, and the slot map already renders them as
 * a grid — which is the right shape for a position anyway.
 *
 * Returns a copy: the underlying array is the cache's own, and a caller sorting
 * it in place would reorder every later read of the same tree.
 */
export function browseChildren(nodes: ApiStorageNode[], parentId: string): ApiStorageNode[] {
  const children = indexOf(nodes).containerChildren.get(parentId)
  return children ? [...children] : []
}

/** A container and everything below it, nested. Slots are never rows — see below. */
export interface ContainerNode extends CountedNode {
  /** The children that are LOADED. `childCount` says how many there are. */
  children: ContainerNode[]
}

/**
 * The whole structure under a unit, nested, for seeing it all at once.
 *
 * The counterpart to `browseChildren`: that one answers "what is in front of
 * me", this one answers "what is in this freezer". Drilling a level at a time
 * keeps the panel short but hides the shape of the thing, and you cannot see
 * where there is room without opening every branch in turn.
 *
 * Positions are still excluded, for the same reason they are excluded from
 * `browseChildren` and for the reason that makes this view usable at all: a
 * dewar of 2 racks × 2 boxes × 9 slots is 6 rows here and 42 if slots were
 * rows. The slot count travels on the parent instead, and the grid is the right
 * shape for looking at slots anyway.
 */
export function containerTree(nodes: CountedNode[], rootId: string): ContainerNode[] {
  const { containerChildren } = indexOf(nodes)
  const build = (parentId: string): ContainerNode[] =>
    (containerChildren.get(parentId) ?? []).map((n) => ({ ...n, children: build(n.id) }))
  return build(rootId)
}

/**
 * Every container id under a root — what "expand all" needs to open.
 *
 * Walks the index rather than `containerTree`, so asking for the ids does not
 * allocate a second copy of the whole tree just to throw it away.
 */
export function containerIds(nodes: ApiStorageNode[], rootId: string): string[] {
  const { containerChildren } = indexOf(nodes)
  const ids: string[] = []
  const walk = (parentId: string) => {
    for (const child of containerChildren.get(parentId) ?? []) {
      ids.push(child.id)
      walk(child.id)
    }
  }
  walk(rootId)
  return ids
}

/**
 * The cells of a grid, when the tree already holds them — otherwise null.
 *
 * Clicking a rack should not wait on the network, and it does not have to: a
 * rack's children are boxes, and the tree carries every container in the unit.
 * The same goes for the unit's own grid of racks. Only a BOX genuinely has to
 * ask, because its children are slots, and a slot has no row until something
 * lands in it — the box's declaration is all that describes it.
 *
 * Returning null means "this one needs the server", so the caller enables its
 * request only then. The effect is that drilling through the structure is
 * instant and only the last step, opening a box, costs a round trip.
 */
export function gridCellsFromTree(
  grid: ApiStorageNode,
  nodes: ApiStorageNode[],
): ApiStorageNode[] | null {
  if (grid.gridRows == null || grid.gridColumns == null) return null
  // Slots are declared, not stored, so the tree cannot have them.
  if (grid.gridChildKind == null || isOccupiable(grid.gridChildKind)) return null
  return browseChildren(nodes, grid.id)
}

/**
 * Most rows the panel will put in the DOM before anyone asks for more.
 *
 * Not a guess at a nice number: it is what separates a tree you can see from
 * one the browser has to lay out. A hundred rows renders instantly, ten
 * thousand freezes the tab for seconds.
 */
export const INITIAL_ROW_BUDGET = 200

/**
 * Which branches to open on arrival, without rendering the whole freezer.
 *
 * The panel used to open the entire first level every time. For a dewar of four
 * racks that is exactly right — the shape of the unit IS the useful thing, and
 * hiding it behind four clicks helps nobody. For a freezer of a hundred racks
 * holding a hundred boxes each, the same rule put ten thousand rows into the
 * DOM before anyone had clicked anything, and that is the freeze: the request
 * for that unit is well under a second, and laying out ten thousand rows is
 * not.
 *
 * So it opens level by level for as long as the result stays inside a budget,
 * and stops at the first level that would exceed it. The top level is always
 * rendered — only its contents wait — so a big unit still shows its racks
 * immediately and opens one when asked.
 *
 * It counts from `childCount` where the server sent one, which is what makes
 * the same rule work when the tree is loaded a level at a time: the decision is
 * "would opening this be too much", and it has to be answerable BEFORE the
 * level is fetched. Counting loaded rows instead would open a hundred racks
 * purely because their ten thousand boxes had not arrived yet.
 */
export function initialExpansion(
  nodes: CountedNode[],
  rootId: string,
  budget: number = INITIAL_ROW_BUDGET,
): Set<string> {
  const { containerChildren } = indexOf(nodes)
  const childrenOf = (id: string) => containerChildren.get(id) ?? []
  const countOf = (node: CountedNode) => node.childCount ?? childrenOf(node.id).length

  const open = new Set<string>()
  let frontier = childrenOf(rootId)
  // The top level is rendered whether or not anything below it opens.
  let visible = frontier.length

  while (frontier.length > 0) {
    // Counted before it is built. Materialising the next level to find out it
    // is too big is the expensive half: a hundred racks means allocating an
    // array of ten thousand boxes purely to discover they will not be drawn.
    let nextCount = 0
    for (const node of frontier) nextCount += countOf(node)
    if (nextCount === 0) break
    if (visible + nextCount > budget) break

    for (const node of frontier) {
      if (countOf(node) > 0) open.add(node.id)
    }
    visible += nextCount
    // Stops of its own accord at a level that has not been fetched yet: its
    // children are not here to walk. The caller fetches what was opened, and
    // this runs again on the larger tree.
    frontier = frontier.flatMap((n) => childrenOf(n.id))
  }
  return open
}

/**
 * True when a node has anything the browser could drill into.
 *
 * Prefers the count the server sent. With a lazily loaded tree an unopened rack
 * holds nothing here, and answering from what is loaded would draw it as a leaf
 * — an expander that never appears on the branch it was meant to open.
 */
export function hasChildren(nodes: CountedNode[], nodeId: string): boolean {
  const node = indexOf(nodes).byId.get(nodeId) as CountedNode | undefined
  if (node?.childCount != null) return node.childCount > 0
  return (indexOf(nodes).containerChildren.get(nodeId)?.length ?? 0) > 0
}

/**
 * The ancestor chain of a node, root first, resolved from the materialised
 * path so it costs no query.
 */
export function crumbsFor(nodes: ApiStorageNode[], nodeId: string): ApiStorageNode[] {
  const { byId, byPath } = indexOf(nodes)
  const node = byId.get(nodeId)
  if (!node) return []
  const segments = node.path.split('/')
  const crumbs: ApiStorageNode[] = []
  // Each ancestor prefix resolved by lookup rather than by re-scanning every
  // node once per segment.
  for (let i = 0; i < segments.length; i++) {
    const ancestor = byPath.get(segments.slice(0, i + 1).join('/'))
    if (ancestor) crumbs.push(ancestor)
  }
  return crumbs
}

/**
 * Where the browser should open when a node is selected from elsewhere.
 *
 * Selecting a rack should LIST that rack's contents; selecting a position
 * should list the box holding it, because a position has nothing to list and
 * showing an empty panel would read as a dead end.
 */
export function levelForSelection(nodes: ApiStorageNode[], nodeId: string): string | null {
  const node = indexOf(nodes).byId.get(nodeId)
  if (!node) return null
  if (isOccupiable(node.kind)) return node.parentId
  return hasChildren(nodes, node.id) ? node.id : node.parentId
}

/**
 * The storage condition in force at a node: its own, or the nearest declared
 * one above it. Mirrors the server's `effectiveCondition`.
 *
 * Most nodes declare nothing — a freezer is certified as a whole — so treating
 * a null on the node itself as "no condition" would report every slot as
 * unclassified and make put-away validation look broken.
 */
export function effectiveConditionFor(
  node: ApiStorageNode,
  nodes: ApiStorageNode[],
): ApiStorageNode['providesCondition'] {
  // The nodes that could supply a condition are exactly this node and its
  // ancestors, and by materialised path those are the prefixes of its own path.
  // Walking them from the deepest down finds the nearest declaration in at most
  // `depth` lookups — the previous full scan cost O(N) per node, which is O(N²)
  // when a panel asks for a row's condition one row at a time.
  const { byPath } = indexOf(nodes)
  const segments = pathSegments(node.path)
  for (let i = segments.length; i > 0; i--) {
    const ancestor = byPath.get(segments.slice(0, i).join('/'))
    if (ancestor?.providesCondition != null) return ancestor.providesCondition
  }
  return null
}
